import apiV2 from '../api/v2';

// Types mirror backend/src/routes/api/v2/billing/mine/*.service.ts exactly -
// dates are ISO strings over JSON, amounts are decimal dollar strings, never cents.

export type DerivedInvoiceStatus = 'upcoming' | 'open' | 'partial' | 'paid' | 'overdue' | 'void';

export interface BillingSummaryKpis {
    outstanding: string;
    outstandingCount: number;
    overdueAmount: string;
    overdueCount: number;
    billed: string;
    billedCount: number;
    paid: string;
    paidRatePct: number | null;
    creditsAvailable: string;
    estimatedRevenue: string;
}

export interface BillingSummaryMonthPoint {
    month: string; // YYYY-MM
    paid: string;
    open: string;
    overdue: string;
}

export type BillingAgingBucketKey = 'current' | '1-30' | '31-60' | '61-90' | '90+';

export interface BillingAgingBucket {
    bucket: BillingAgingBucketKey;
    amount: string;
    count: number;
}

export interface BillingSummary {
    kpis: BillingSummaryKpis;
    monthly: BillingSummaryMonthPoint[];
    aging: BillingAgingBucket[];
}

export interface BillingInvoiceChargeSummary {
    id: string;
    description: string | null;
    productSummary: string | null;
    autoTrigger: string | null;
    patientId: string | null;
    patientName: string | null;
    totalAmount: string;
}

export interface BillingInvoiceListItem {
    id: string;
    invoiceNumber: string;
    installmentIndex: number;
    installmentCount: number;
    amount: string;
    dueDate: string;
    status: string;
    derivedStatus: DerivedInvoiceStatus;
    daysLate: number | null;
    paidAt: string | null;
    createdAt: string;
    charge: BillingInvoiceChargeSummary;
}

export interface BillingInvoiceLineItem {
    description: string;
    unitPrice: string;
    quantity: number;
    lineTotal: string;
}

export interface BillingInvoiceCredit {
    description: string;
    amount: string;
}

export interface BillingInvoiceScheduleItem {
    installmentIndex: number;
    amount: string;
    dueDate: string;
    derivedStatus: DerivedInvoiceStatus;
}

export interface BillingInvoiceDetail extends BillingInvoiceListItem {
    lineItems: BillingInvoiceLineItem[];
    discountType: string | null;
    discountValue: string | null;
    creditsApplied: BillingInvoiceCredit[];
    schedule: BillingInvoiceScheduleItem[];
}

export interface BillingPayLink {
    paymentLink: string;
}

export async function fetchBillingSummary(from?: string, to?: string): Promise<BillingSummary> {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiV2.get<BillingSummary>('/billing/mine/summary', { params });
    return response.data;
}

export async function fetchBillingInvoices(): Promise<BillingInvoiceListItem[]> {
    const response = await apiV2.get<BillingInvoiceListItem[]>('/billing/mine/invoices');
    return response.data;
}

export async function fetchBillingInvoice(invoiceId: string): Promise<BillingInvoiceDetail> {
    const response = await apiV2.get<BillingInvoiceDetail>(`/billing/mine/invoices/${invoiceId}`);
    return response.data;
}

export async function createBillingPayLink(invoiceId: string): Promise<BillingPayLink> {
    const response = await apiV2.post<BillingPayLink>(
        `/billing/mine/invoices/${invoiceId}/pay-link`
    );
    return response.data;
}

export interface BillingInvoicesExportParams {
    from?: string;
    to?: string;
}

export interface BillingInvoicesExportResult {
    blob: Blob;
    truncated: boolean;
}

// practiceId/patientId are deliberately not accepted - the practice is derived
// server-side from the auth token, which is this endpoint's scoping guarantee.
export async function exportBillingInvoicesCsv(
    params: BillingInvoicesExportParams
): Promise<BillingInvoicesExportResult> {
    const response = await apiV2.get<Blob>('/billing/mine/invoices/export', {
        params: {
            ...(params.from ? { from: params.from } : {}),
            ...(params.to ? { to: params.to } : {})
        },
        responseType: 'blob'
    });
    return {
        blob: response.data,
        truncated: response.headers['x-export-truncated'] === 'true'
    };
}
